import './loanDocumentMileagePage.scss';

import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { red } from '@mui/material/colors';
import LinearProgress from '@mui/material/LinearProgress';
import { ThemeProvider } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import isEmpty from 'lodash/isEmpty';
import toString from 'lodash/toString';
import { ReactNode, useEffect } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { inputEventHandler } from '../../../components/utils/inputEventHandler';
import { FeatureFlag } from '../../../store/featureStore/enums/FeatureFlag';
import { useFeatureFlag } from '../../../store/featureStore/hooks/useFeatureFlag';
import useNewLoanDocumentStore from '../../../store/loanDocumentStore/useNewLoanDocumentStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import { useNavigateWithCallback } from '../../../store/routeStore/hooks/useNavigateWithCallback';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();
  const navigateWithCallback = useNavigateWithCallback();

  const { setDrawerOpen } = useNavigationPaneStore();

  const { t } = useTranslation();

  const isSimplifiedWorkflowEnabled = useFeatureFlag(
    FeatureFlag.ENABLE_SIMPLIFIED_LOAN_DOCUMENTS_WORKFLOW,
  );

  const {
    clearAllDocuments,
    isUploadFinished,
    isUploadSuccess,
    isUploading,
    mileage,
    setMileage,
    uploadDocuments,
    uploadFailedMessage,
  } = useNewLoanDocumentStore();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onBlur',
    criteriaMode: 'firstError',
    defaultValues: {
      mileageValue: mileage,
    },
    delayError: 200,
  });

  const leadingZeroValidation = (
    value: string,
    error: string,
  ): string | boolean => {
    if (value.length > 1 && value.startsWith('0')) {
      return error;
    }
  };

  const mileageValidation = {
    required: t('validation.mileage.required'),
    validate: (value: string): string | boolean =>
      leadingZeroValidation(value, t('validation.mileage.invalid')),
  };

  useEffect((): void => {
    if (isUploadFinished && isUploadSuccess) {
      navigateWithCallback('/loan-documents/success').then((): void => {
        clearAllDocuments();
      });
    }
  }, [isUploadFinished, isUploadSuccess]);

  const previousStep = (): void => {
    if (isSimplifiedWorkflowEnabled) {
      navigate('/loan-documents/document-upload');
      return;
    }

    navigate('/loan-documents/list');
  };

  const formSubmit = (values: FieldValues): Promise<void> => {
    const { mileageValue } = values;

    if (!mileageValue) {
      return;
    }

    setMileage(mileageValue);

    uploadDocuments();
  };

  return (
    <div className="loan-document-mileage-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <header data-testid={locator.pages.loanDocumentMileage.header.root}>
            <button
              aria-label="Previous step"
              data-testid={
                locator.pages.loanDocumentMileage.header.previousStepButton
              }
              type="button"
              onClick={previousStep}
            >
              <KeyboardBackspaceRoundedIcon />
            </button>
            <button
              aria-label="Toggle drawer"
              data-testid={
                locator.pages.loanDocumentMileage.header.drawerButton
              }
              type="button"
              onClick={(): void => {
                setDrawerOpen(true);
              }}
            >
              <MenuRoundedIcon />
            </button>
          </header>

          <div className="progress-container" role="complementary">
            <LinearProgress
              aria-label="Application completed percentage"
              data-testid={locator.pages.loanDocumentMileage.progress.root}
              value={90}
              variant="determinate"
            />
          </div>

          <main>
            <form
              data-testid={locator.pages.loanDocumentMileage.form.root}
              noValidate
              onSubmit={handleSubmit(formSubmit)}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.loanDocumentMileage.form.h1}>
                  {t('LoanDocumentMileagePage.title')}
                </h1>
                <h2 data-testid={locator.pages.loanDocumentMileage.form.h2}>
                  {t('LoanDocumentMileagePage.subtitle')}
                </h2>

                {!isUploading && !isEmpty(uploadFailedMessage) && (
                  <div style={{ marginBottom: '2rem' }}>
                    <Alert
                      data-testid={
                        locator.pages.loanDocumentMileage.alert.error
                      }
                      icon={false}
                      severity="error"
                    >
                      {uploadFailedMessage}
                    </Alert>
                  </div>
                )}

                <div style={{ marginBottom: '3rem' }}>
                  <TextField
                    {...register('mileageValue', mileageValidation)}
                    error={!isEmpty(errors.mileageValue)}
                    label={t('LoanDocumentMileagePage.mileage')}
                    slotProps={{
                      htmlInput: {
                        'data-testid':
                          locator.pages.loanDocumentMileage.form.mileageInput,
                        inputMode: 'numeric',
                        min: 0,
                      },
                    }}
                    type="number"
                    variant="outlined"
                    autoFocus
                    fullWidth
                    onKeyDown={inputEventHandler.number.keydown}
                    onPasteCapture={inputEventHandler.number.pasteCapture}
                  />

                  {!isEmpty(errors.mileageValue) && (
                    <span
                      data-testid={
                        locator.pages.loanDocumentMileage.form.mileageInputError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.mileageValue.message)}
                    </span>
                  )}
                </div>

                <Button
                  color="primary"
                  data-testid={
                    locator.pages.loanDocumentMileage.form.submitButton
                  }
                  disabled={!isValid}
                  loading={isUploading}
                  loadingPosition="start"
                  startIcon={<></>}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  {t('LoanDocumentMileagePage.submit')}
                </Button>
              </div>
              <footer
                data-testid={locator.pages.loanDocumentMileage.footer.root}
              >
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>

        <NavigationPane withDrawer withUserNameHeader />
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'LoanDocumentMileagePage';
